import type { FirmAdminSidebarNavigationService } from '.';
import { computed, signal } from '@preact/signals-react';
import type { Signal } from '@preact/signals-react';
import type { FirmWithParentsAndChildren } from '@thinkalpha/ui-types/types/admin/firms';
import { inject, injectable } from 'inversify';
import { UnreachableCaseError } from 'src/lib/util/unreachableCaseError';
import type { ReactBindings } from 'src/types/bindings';

interface FirmAdminSidebarNavigationState {
    locationPathname: string;
    firms: FirmWithParentsAndChildren[];
    // TODO: Change to active firm ID
    activeCollapsibleIndex: number;
    // TODO: Change to open firm IDs
    openFirmIndexes: number[];
}

type FirmAdminSidebarNavigationAction =
    | { type: 'SET_LOCATION_PATHNAME'; locationPathname: string }
    | { type: 'SET_FIRMS'; firms: FirmWithParentsAndChildren[] }
    | { type: 'SET_OPEN_STATE'; index: number; open: boolean };

export function firmAdminSidebarNavigationStateReducer(
    state: FirmAdminSidebarNavigationState,
    action: FirmAdminSidebarNavigationAction,
): FirmAdminSidebarNavigationState {
    switch (action.type) {
        case 'SET_OPEN_STATE':
            if (action.open) {
                if (state.openFirmIndexes.includes(action.index)) {
                    return state;
                }

                return {
                    ...state,
                    openFirmIndexes: [...state.openFirmIndexes, action.index],
                };
            }

            if (!action.open) {
                if (!state.openFirmIndexes.includes(action.index)) {
                    return state;
                }

                return {
                    ...state,
                    openFirmIndexes: state.openFirmIndexes.filter(
                        (i) => i !== action.index || i === state.activeCollapsibleIndex,
                    ),
                };
            }
            return state;
        case 'SET_FIRMS': {
            const currentPathname = state.locationPathname;
            const activeFirmIndex = action.firms.findIndex((firm) => currentPathname?.includes(firm.id));

            return {
                ...state,
                firms: action.firms,
                activeCollapsibleIndex: activeFirmIndex,
                openFirmIndexes: activeFirmIndex !== -1 ? [activeFirmIndex] : [],
            };
        }
        case 'SET_LOCATION_PATHNAME': {
            if (state.locationPathname === action.locationPathname) {
                return state;
            }

            const activeFirmIndex = state.firms.findIndex((firm) => action.locationPathname?.includes(firm.id));

            return {
                ...state,
                locationPathname: action.locationPathname,
                activeCollapsibleIndex: activeFirmIndex,
                openFirmIndexes: activeFirmIndex !== -1 ? [activeFirmIndex] : [],
            };
        }
        default:
            throw new UnreachableCaseError(action);
    }
}

@injectable()
export class FirmAdminSidebarNavigationServiceImpl implements FirmAdminSidebarNavigationService {
    #disposableStack = new DisposableStack();
    #store: ReactBindings['Store'];
    #logger: ReactBindings['Logger'];
    #navigationState: Signal<FirmAdminSidebarNavigationState>;

    constructor(@inject('Store') store: ReactBindings['Store'], @inject('Logger') logger: ReactBindings['Logger']) {
        this.#store = store;
        this.#logger = logger;

        const initialState: FirmAdminSidebarNavigationState = {
            locationPathname: this.#store.getState().router.location?.pathname ?? '',
            firms: [],
            activeCollapsibleIndex: -1,
            openFirmIndexes: [],
        };
        this.#navigationState = signal(initialState);

        this.#disposableStack.defer(
            this.#store
                .subscribeSelector((state) => state.router.location?.pathname)
                .handle((pathname) => {
                    this.#dispatch({ type: 'SET_LOCATION_PATHNAME', locationPathname: pathname ?? '' });
                }),
        );
    }

    #dispatch(action: FirmAdminSidebarNavigationAction) {
        this.#navigationState.value = firmAdminSidebarNavigationStateReducer(this.#navigationState.value, action);
    }

    setFirms(firms: FirmWithParentsAndChildren[]) {
        this.#dispatch({ type: 'SET_FIRMS', firms });
    }

    #activeCollapsibleIndexSignal = computed<number>(() => {
        return this.#navigationState.value.activeCollapsibleIndex;
    });
    get activeCollapsibleIndexSignal() {
        return this.#activeCollapsibleIndexSignal;
    }

    #openStateSignal = computed<number[]>(() => {
        return this.#navigationState.value.openFirmIndexes;
    });
    get openStateSignal() {
        return this.#openStateSignal;
    }

    onOpenStateChange(selectedIndex: number, open: boolean): void {
        this.#dispatch({ type: 'SET_OPEN_STATE', index: selectedIndex, open });
    }

    [Symbol.dispose](): void {
        this.#disposableStack.dispose();
    }
}
